import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useReservationResultStore } from '@/stores/reservation-result-store';

import CustomerInfo from '../features/confirm/components/CustomerInfo';
import Header from '../features/confirm/components/Header';
import OrderSummary from '../features/confirm/components/OrderSummary';
import PaymentInfo from '../features/confirm/components/PaymentInfo';
import ReservationInfo from '../features/confirm/components/ReservationInfo';

export default function ConfirmPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const { currentReservation } = useReservationResultStore();

  // 예약 데이터가 없으면 장바구니 페이지로 리다이렉트
  useEffect(() => {
    console.log('useEffect 실행 - currentReservation:', currentReservation);
    if (!currentReservation) {
      console.warn('예약 데이터가 없습니다. 장바구니 페이지로 이동합니다.');
      navigate('/cart', { replace: true });
    }
  }, [currentReservation, navigate]);

  // 예약 데이터가 없으면 로딩 상태 표시
  if (!currentReservation) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-neutral-500">예약 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // OrderSummary 컴포넌트에 맞는 형태로 데이터 변환
  const orderItems = currentReservation.items.map(item => ({
    name: item.name,
    options: item.option || '',
    price: item.price,
    quantity: item.quantity,
  }));

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* NavBar */}
      <Header title="예약 신청 완료" />

      <main className="flex-grow">
        <section className="px-5 py-8 space-y-6">
          {/* 예약번호 영역 */}
          <ReservationInfo reservationNumber={currentReservation.reservationNumber} />

          {/* 고객 정보 영역 */}
          <CustomerInfo
            name={currentReservation.formData.name}
            phone={currentReservation.formData.phone}
            pickupDateTime={currentReservation.pickupDateTime}
          />

          {/* 입금 안내 메시지 */}
          <div className="text-center text-neutral-700">
            <p className="text-base font-semibold">아래 계좌번호로 입금을 완료해주시면</p>
            <p className="text-base font-semibold">예약이 확정됩니다!</p>
          </div>

          {/* 입금 정보 */}
          <PaymentInfo
            amount={currentReservation.totalAmount}
            bankName={currentReservation.paymentInfo.bankName}
            accountNumber={currentReservation.paymentInfo.accountNumber}
            accountHolder={currentReservation.paymentInfo.accountHolder}
          />

          <section>
            <div className="flex items-center gap-2 p-4 bg-[#f0f6ff] rounded-md">
              <img src="/icons/warning.svg" alt="info" width={18} height={18} />
              <div className="flex flex-col gap-2">
                <div className="text-xs font-semibold text-[#3D8AFF]">
                  주문 상세 링크로 언제든지 내역을 다시 확인할 수 있어요.
                </div>
                {currentReservation.publicToken && (
                  <Link
                    to={`/order/${currentReservation.publicToken}`}
                    className="text-xs font-semibold text-[#3D8AFF] underline underline-offset-2"
                  >
                    주문 상세 보기
                  </Link>
                )}
              </div>
            </div>
          </section>
        </section>

        <div className="h-1.5 bg-gray-100" />

        {/* 예약한 상품 목록 */}
        <section className="px-5 py-8">
          <OrderSummary items={orderItems} totalAmount={currentReservation.totalAmount} />
        </section>
      </main>
    </div>
  );
}
